import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { getErrorWithAction, formatErrorMessage } from '@repo/shared-utils';
import {
  onboardingPlaidApi,
  trustAccountingPlaidFromExchange,
} from '../../services/api/onboardingPlaid';
import type { TrustAccountingPlaidData } from '../../services/api/auth';

export type PmOnboardingPlaidLinkProps = {
  /** Called with operating-bank processor payload after Plaid Link + server exchange. */
  onLinked: (trustAccountingPlaid: TrustAccountingPlaidData) => void;
  /** When user already entered manual ACH in the wizard. */
  canSkip?: boolean;
  onSkip?: () => void;
  onError?: (message: string) => void;
};

/**
 * Plaid Link for PM pre-profile onboarding (requires Clerk session; server mints link_token).
 */
export const PmOnboardingPlaidLink: React.FC<PmOnboardingPlaidLinkProps> = ({
  onLinked,
  canSkip = false,
  onSkip,
  onError,
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await onboardingPlaidApi.createLinkToken();
        if (!cancelled) setLinkToken(res.link_token);
      } catch (e) {
        const msg = formatErrorMessage(getErrorWithAction(e));
        if (!cancelled) {
          setLoadError(msg);
          onError?.(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  const onSuccess = useCallback(
    (publicToken: string, metadata: { accounts?: { id: string }[] }) => {
      void (async () => {
        const accountId = metadata.accounts?.[0]?.id;
        if (!accountId) {
          const msg = 'No bank account was selected. Please try again and pick an account.';
          onError?.(msg);
          return;
        }
        setBusy(true);
        try {
          const out = await onboardingPlaidApi.exchangePublicToken({
            publicToken: publicToken,
            accountId,
          });
          onLinked(trustAccountingPlaidFromExchange(out));
        } catch (e) {
          onError?.(formatErrorMessage(getErrorWithAction(e)));
        } finally {
          setBusy(false);
        }
      })();
    },
    [onError, onLinked],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
  });

  return (
    <div className="rounded-xl border border-slate-600 bg-slate-800/80 p-4 space-y-3 text-left max-w-md w-full">
      <h2 className="text-sm font-semibold text-white">Link your operating bank</h2>
      <p className="text-xs text-slate-400">
        Securely connect via Plaid. We never store your full account or routing numbers from this flow—only a
        processor token for payouts.
      </p>
      {loadError ? <p className="text-xs text-red-400">{loadError}</p> : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <button
          type="button"
          disabled={!ready || !linkToken || !!loadError || busy}
          onClick={() => open()}
          className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
        >
          {busy ? 'Connecting…' : 'Link with Plaid'}
        </button>
        {canSkip && onSkip ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onSkip()}
            className="px-4 py-2.5 rounded-lg border border-slate-500 text-slate-200 text-sm hover:bg-slate-700/50"
          >
            {canSkip ? 'Skip for now' : 'Skip'}
          </button>
        ) : null}
      </div>
    </div>
  );
};
